import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthenticationService } from '../services/authentication.service';
import { roles } from '../utils/constants';
import { getRolesForImpersonatedEmployee } from '../utils/constants/roles';
import api from '../services/api';
import logger from '../utils/logger';

const ImpersonationContext = createContext({
    impersonatedUser: null,
    impersonationRoles: [],
    employees: [],
    isLoadingEmployees: false,
    setImpersonatedUser: () => {},
    clearImpersonation: () => {},
    refreshEmployees: () => {},
    isAdmin: false,
    isImpersonationReady: false,
});

export function ImpersonationProvider({ children }) {
    const auth = useAuthenticationService();
    const isAdmin = auth.isInAnyRole([roles.ROLE_ADMINISTRATOR, roles.ROLE_SUPER_ADMINISTRATOR]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [impersonatedUser, setImpersonatedUserState] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
    // Indicates whether impersonation state has been resolved from the URL.
    // Data fetches must wait for this so a hard refresh uses the impersonated
    // user's email rather than the signed-in user's.
    const [isImpersonationReady, setIsImpersonationReady] = useState(false);
    const cancelRef = useRef(null);

    const refreshEmployees = useCallback(() => {
        if (!isAdmin) return;

        if (cancelRef.current) cancelRef.current();
        let cancelled = false;
        cancelRef.current = () => { cancelled = true; };

        setIsLoadingEmployees(true);

        api.getEmployees()
            .then((response) => {
                if (cancelled) return;
                setEmployees(Array.isArray(response) ? response : []);
            })
            .catch((error) => {
                if (cancelled) return;
                logger.error('Error loading employees for impersonation:', error);
            })
            .finally(() => {
                if (!cancelled) setIsLoadingEmployees(false);
            });
    }, [isAdmin]);

    useEffect(() => {
        refreshEmployees();
        return () => {
            if (cancelRef.current) cancelRef.current();
        };
    }, [refreshEmployees]);

    // Restore impersonation from URL when employees finish loading, and flag
    // when impersonation has been resolved so dependent fetches can proceed.
    useEffect(() => {
        // Non-admins can't impersonate — nothing to restore.
        if (!isAdmin) {
            setIsImpersonationReady(true);
            return;
        }
        const emailFromUrl = searchParams.get('impersonate');
        // No impersonation requested — ready immediately.
        if (!emailFromUrl) {
            setIsImpersonationReady(true);
            return;
        }
        // Impersonation requested — wait for the employee list before resolving
        // so data fetches use the impersonated user's email.
        if (employees.length === 0) return;

        const match = employees.find(
            (emp) => emp.email?.toLowerCase() === emailFromUrl.toLowerCase(),
        );
        if (match) {
            setImpersonatedUserState(match);
        }
        setIsImpersonationReady(true);
    }, [isAdmin, employees, searchParams]);

    const setImpersonatedUser = useCallback((user) => {
        setImpersonatedUserState(user);
        if (user?.email) {
            setSearchParams((prev) => {
                prev.set('impersonate', user.email);
                return prev;
            }, { replace: true });
        }
    }, [setSearchParams]);

    const clearImpersonation = useCallback(() => {
        setImpersonatedUserState(null);
        setSearchParams((prev) => {
            prev.delete('impersonate');
            return prev;
        }, { replace: true });
    }, [setSearchParams]);

    const impersonationRoles = useMemo(
        () => (impersonatedUser ? getRolesForImpersonatedEmployee(impersonatedUser) : []),
        [impersonatedUser],
    );

    return (
        <ImpersonationContext.Provider value={{
            impersonatedUser,
            impersonationRoles,
            employees,
            isLoadingEmployees,
            setImpersonatedUser,
            clearImpersonation,
            refreshEmployees,
            isAdmin,
            isImpersonationReady,
        }}>
            {children}
        </ImpersonationContext.Provider>
    );
}

export function useImpersonation() {
    return useContext(ImpersonationContext);
}

export default ImpersonationContext;
